/**
 * Sidebar highlight rules driven by explicit route scope (entry context), not URL stack equality.
 */
import { ASSET_ROOT_CANONICAL_ER_SCREEN_IDS, type MenuEntry } from '@buxus/shared'
import {
  isAssetContentScreen,
  isErSubtreeScreen,
  type OperatorContentRoute,
  type RouteScope,
  routeFromMenuStack,
} from './operatorRoute'

export type NavHighlightContext = {
  scope: RouteScope
  screenId: string
  assetId: string | null
}

export function highlightContextFromRoute(route: OperatorContentRoute): NavHighlightContext {
  return {
    scope: route.scope,
    screenId: route.screenId,
    assetId: route.assetId,
  }
}

export function highlightContextFromStack(
  stack: string[],
  assetId: string | null,
): NavHighlightContext {
  const route = routeFromMenuStack(stack, assetId)
  return highlightContextFromRoute(route)
}

/** Which sidebar entry path a menu node belongs to (operator / assets / in-an-emergency). */
export function menuEntryScope(node: MenuEntry): RouteScope {
  const ns = node.navStack ?? []
  if (ns.includes('screen.inAnEmergency')) return 'in-an-emergency'
  if (node.assetId != null || ns.includes('screen.assets')) return 'assets'
  return 'operator'
}

/** Sidebar ER folder rows live under Assets; their navStack includes the ER hub screen id. */
function isAssetBranchErNode(node: MenuEntry): boolean {
  return Boolean(node.navStack?.includes('screen.assetEmergencyResponse'))
}

export function menuLeafIsActive(node: MenuEntry, ctx: NavHighlightContext): boolean {
  if (!node.screenId || node.screenId !== ctx.screenId) return false

  const nodeAsset = node.assetId ?? null
  const nodeScope = menuEntryScope(node)

  if (nodeAsset !== null) {
    if (ctx.assetId === null || nodeAsset !== ctx.assetId) return false
  } else if (ctx.assetId !== null && isAssetContentScreen(ctx.screenId)) {
    if (ctx.screenId !== 'screen.assets') return false
  }

  if (
    ctx.scope === 'in-an-emergency' &&
    ASSET_ROOT_CANONICAL_ER_SCREEN_IDS.has(ctx.screenId)
  ) {
    return (
      nodeScope === 'assets' &&
      !node.navStack?.includes('screen.assetEmergencyResponse')
    )
  }

  /**
   * In-an-emergency entry URLs use a different scope than the Assets-branch navStack, but the
   * sidebar keeps a single ER subtree under Assets > {asset}. Match those rows by screen + asset.
   */
  if (
    ctx.scope === 'in-an-emergency' &&
    isAssetBranchErNode(node) &&
    (isErSubtreeScreen(ctx.screenId) || ctx.screenId === 'screen.assetEmergencyResponse')
  ) {
    return true
  }

  if (nodeScope === 'operator' && ctx.scope === 'operator') return true
  if (nodeScope === ctx.scope) return true

  if (
    node.screenId === 'screen.inAnEmergency' &&
    ctx.screenId === 'screen.inAnEmergency' &&
    ctx.scope === 'in-an-emergency'
  ) {
    return true
  }

  return false
}

/** ER hub folder gets ancestor styling when viewing a deeper ER screen via in-an-emergency entry. */
export function isErHubAncestorForRoute(
  node: MenuEntry,
  ctx: NavHighlightContext,
): boolean {
  if (node.screenId !== 'screen.assetEmergencyResponse') return false
  if (ctx.scope !== 'in-an-emergency') return false
  if (ctx.screenId === 'screen.assetEmergencyResponse') return false
  return isErSubtreeScreen(ctx.screenId)
}

/** Folder-only hub (e.g. Emergency Response) active when viewing the hub screen, not a subpage. */
export function isNavHubFolderForScreen(
  node: MenuEntry,
  screenId: string,
  activeAssetId: string | null,
): boolean {
  if (node.screenId || !(node.children?.length)) return false
  if (node.assetId != null && activeAssetId != null && node.assetId !== activeAssetId) {
    return false
  }
  return node.children.some((c) => {
    const ns = c.navStack
    if (!ns || ns.length < 2) return false
    const hubScreenId = ns[ns.length - 2]!
    return hubScreenId === screenId && c.screenId !== screenId
  })
}

export function navTreeDescendantMatchesActive(
  node: MenuEntry,
  ctx: NavHighlightContext,
): boolean {
  for (const c of node.children ?? []) {
    if (menuLeafIsActive(c, ctx)) return true
    if (navTreeDescendantMatchesActive(c, ctx)) return true
  }
  return false
}

export function findActivePathIdsInTree(
  nodes: MenuEntry[],
  ctx: NavHighlightContext,
): string[] | null {
  for (const node of nodes) {
    const childPath = node.children?.length
      ? findActivePathIdsInTree(node.children, ctx)
      : null
    const selfActive =
      menuLeafIsActive(node, ctx) ||
      isNavHubFolderForScreen(node, ctx.screenId, ctx.assetId)
    if (childPath && childPath.length) return [node.id, ...childPath]
    if (selfActive) return [node.id]
  }
  return null
}
